import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation, Link, NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Sun, 
  Moon, 
  Menu, 
  X, 
  User, 
  Layers, 
  Trophy, 
  FolderDown, 
  MessageSquare, 
  Sparkles, 
  UploadCloud, 
  ShoppingBag, 
  Bell, 
  CheckCircle2, 
  ShieldCheck, 
  Settings as SettingsIcon, 
  LogOut, 
  Search, 
  ArrowRight,
  Command,
  HardDrive
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { fetchNotifications, markAllNotificationsRead } from '../api/client';
import BuildiveLogo from './BuildiveLogo';
import Button from './Button';
import CartDrawer from './CartDrawer';
import './Navbar.css';

export default function Navbar({ onOpenAuth }) {
  const { theme, toggleTheme } = useTheme();
  const { user, dbUser, signOut } = useAuth();
  const { itemCount, setIsCartOpen } = useCart();
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Notifications query
  const { data: notifData } = useQuery({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    enabled: !!user,
    refetchInterval: 12000,
  });

  const notifications = notifData?.notifications || [];
  const unreadCount = notifData?.unreadCount || 0;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchFocused(false);
      setMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { name: 'Marketplace', path: '/browse', icon: Layers },
    { name: 'Challenges', path: '/competitions', icon: Trophy, hasPulse: true },
    { name: 'Community', path: '/community', icon: MessageSquare },
    { name: 'About', path: '/about', icon: Sparkles }
  ];

  const quickTrendingTags = ['UI Kit', 'Figma Tokens', '3D Blender', 'Fintech', 'Branding'];

  return (
    <>
      <header className="navbar-wrapper">
        <nav className="navbar-container">
          
          {/* Left: Brand Logo & Navigation Links */}
          <div className="navbar-left">
            <Link to="/" className="navbar-logo-link" title="Buildive Home">
              <BuildiveLogo size={30} />
            </Link>

            <div className="navbar-links hidden-tablet">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) => `navbar-nav-item ${isActive ? 'active' : ''}`}
                >
                  <link.icon size={15} />
                  <span>{link.name}</span>
                  {link.hasPulse && <span className="nav-live-dot" title="Live Bounties Active" />}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Center: Command Search Input */}
          <div className={`navbar-search-wrapper hidden-mobile ${searchFocused ? 'focused' : ''}`}>
            <form onSubmit={handleSearchSubmit} className="navbar-search-bar">
              <Search size={15} className="search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search UI kits, 3D mockups, creators..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                className="navbar-search-input"
              />
              <kbd className="search-kbd-chip">
                <Command size={11} /> K
              </kbd>
            </form>

            {/* Quick Search Suggestions Popover */}
            {searchFocused && (
              <div className="search-popover-menu animate-fade-in">
                <div className="popover-section-label">TRENDING DISCOVERIES</div>
                <div className="popover-tags-row">
                  {quickTrendingTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onMouseDown={() => {
                        setSearchQuery(tag);
                        navigate(`/browse?q=${encodeURIComponent(tag)}`);
                        setSearchFocused(false);
                      }}
                      className="popover-tag-chip"
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Actions, Cart, Theme Toggle & User Profile */}
          <div className="navbar-right">
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="navbar-action-btn tactile-btn"
              title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} mode`}
            >
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>

            {/* Cart Drawer Trigger */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="navbar-action-btn tactile-btn cart-trigger"
              title="Shopping Cart"
            >
              <ShoppingBag size={17} />
              {itemCount > 0 && <span className="cart-badge-counter">{itemCount}</span>}
            </button>

            {/* Notifications Dropdown (When Logged in) */}
            {user && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setNotifDropdownOpen(!notifDropdownOpen)}
                  className="navbar-action-btn tactile-btn"
                  title="Notifications"
                >
                  <Bell size={17} />
                  {unreadCount > 0 && <span className="notif-badge-dot" />}
                </button>
              </div>
            )}

            {/* Creator Studio & Upload Shortcut */}
            <button
              onClick={() => navigate('/dashboard/upload')}
              className="navbar-publish-btn tactile-btn hidden-mobile"
            >
              <UploadCloud size={15} />
              <span>Publish</span>
            </button>

            {/* User Profile / Auth Action */}
            {user ? (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="user-avatar-btn tactile-btn"
                >
                  <img
                    src={dbUser?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.id || 'creator'}`}
                    alt="Profile"
                    className="user-avatar-img"
                  />
                </button>

                {/* Profile Dropdown */}
                {dropdownOpen && (
                  <div className="profile-dropdown-menu animate-fade-in" onClick={() => setDropdownOpen(false)}>
                    <div className="dropdown-user-header">
                      <strong style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                        {dbUser?.display_name || user.email?.split('@')[0]}
                      </strong>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{user.email}</span>
                    </div>

                    <div className="dropdown-divider" />

                    <Link to="/dashboard" className="dropdown-menu-item">
                      <Layers size={15} /> Creator Studio
                    </Link>
                    <Link to="/library" className="dropdown-menu-item">
                      <HardDrive size={15} /> Asset Vault
                    </Link>
                    <Link to="/settings" className="dropdown-menu-item">
                      <SettingsIcon size={15} /> Settings & Profile
                    </Link>

                    <div className="dropdown-divider" />

                    <button onClick={signOut} className="dropdown-menu-item logout">
                      <LogOut size={15} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button variant="primary" size="sm" onClick={onOpenAuth}>
                Join / Sign In
              </Button>
            )}

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="navbar-hamburger-btn hidden-desktop"
              title="Menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Drawer Menu */}
      {mobileMenuOpen && (
        <div className="mobile-nav-drawer animate-fade-in">
          <form onSubmit={handleSearchSubmit} style={{ marginBottom: '20px' }}>
            <div className="navbar-search-bar">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search templates & challenges..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="navbar-search-input"
              />
            </div>
          </form>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {navLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="mobile-nav-link"
              >
                <link.icon size={18} />
                <span>{link.name}</span>
              </NavLink>
            ))}
            <Link to="/dashboard/upload" onClick={() => setMobileMenuOpen(false)} className="mobile-nav-link publish">
              <UploadCloud size={18} />
              <span>Publish Template</span>
            </Link>
          </div>
        </div>
      )}

      {/* Cart Drawer Component */}
      <CartDrawer onCheckout={() => {}} />
    </>
  );
}
